// src/features/locker-assignments/components/forms/LockerRequirementsSection.tsx
import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  FormHelperText,
} from '@mui/material';
import { Archive as ArchiveIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { MyFormSection } from '@/components';
import type { LockerRequirement, ValidationErrors } from '../../types';

interface LockerRequirementsSectionProps {
  lockerRequirements: LockerRequirement[];
  loadingHotelData: boolean;
  validationErrors: ValidationErrors;
  onLockerRequirementChange: (index: number, value: number) => void;
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
}

export const LockerRequirementsSection: React.FC<LockerRequirementsSectionProps> = ({
  lockerRequirements,
  loadingHotelData,
  validationErrors,
  onLockerRequirementChange,
  setValidationErrors,
}) => {
  const translate = useTranslate();

  const handleQuantityChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    onLockerRequirementChange(index, numValue);
    
    // Limpiar error cuando el usuario empieza a corregir
    if (validationErrors.lockerRequirements && numValue > 0) {
      setValidationErrors({});
    }
  };

  if (loadingHotelData) {
    return (
      <MyFormSection title={translate('resources.locker-assignments.messages.locker_requirements_title')} icon={<ArchiveIcon />}>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.locker-assignments.messages.loading_locker_types')}
        </Typography>
      </MyFormSection>
    );
  }

  if (lockerRequirements.length === 0) {
    return (
      <MyFormSection title={translate('resources.locker-assignments.messages.locker_requirements_title')} icon={<ArchiveIcon />}>
        <Alert severity="info">
          {translate('resources.locker-assignments.messages.no_locker_types_configured')}
        </Alert>
      </MyFormSection>
    );
  }

  const isSingleLockerType = lockerRequirements.length === 1;

return (
  <MyFormSection
    title={translate('resources.locker-assignments.messages.locker_requirements_title')}
    icon={<ArchiveIcon />}
  >
    {validationErrors.lockerRequirements && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {validationErrors.lockerRequirements}
      </Alert>
    )}

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {lockerRequirements.map((req, index) => {
        const isLocked = isSingleLockerType && index === 0; // Only one type -> lock first

        return (
          <Box
            key={req.lockerTypeId}
            sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
          >
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
              {req.typeName}
            </Typography>
            <TextField
              type="number"
              value={isLocked ? 1 : req.quantity}
              onChange={
                isLocked
                  ? undefined
                  : (e) => handleQuantityChange(index, e.target.value)
              }
              disabled={isLocked}
              InputProps={{ inputProps: { min: 0 } }}
              variant="outlined"
              size="small"
              sx={{ width: 80 }}
              error={
                !isLocked &&
                !!validationErrors.lockerRequirements &&
                req.quantity === 0
              }
            />
          </Box>
        );
      })}
    </Box>

    <FormHelperText error={!!validationErrors.lockerRequirements}>
      {translate(
        'resources.locker-assignments.messages.must_request_locker_help'
      )}
    </FormHelperText>
  </MyFormSection>
);

}

